/**
 * US CPI-U annual averages (1982-84=100).
 * Source: Bureau of Labor Statistics (BLS)
 * @see https://www.bls.gov/cpi/
 * Last updated: 2026-03-12 (run `npm run update:cpi` to refresh)
 */

export const CPI_BY_YEAR: Record<number, number> = {
  1913: 9.88,
  1914: 10.02,
  1915: 10.11,
  1916: 10.88,
  1917: 12.82,
  1918: 15.04,
  1919: 17.33,
  1920: 20.04,
  1921: 17.85,
  1922: 16.75,
  1923: 17.05,
  1924: 17.13,
  1925: 17.54,
  1926: 17.7,
  1927: 17.36,
  1928: 17.16,
  1929: 17.16,
  1930: 16.7,
  1931: 15.21,
  1932: 13.64,
  1933: 12.93,
  1934: 13.38,
  1935: 13.73,
  1936: 13.87,
  1937: 14.38,
  1938: 14.09,
  1939: 13.91,
  1940: 14.01,
  1941: 14.73,
  1942: 16.33,
  1943: 17.31,
  1944: 17.59,
  1945: 17.99,
  1946: 19.52,
  1947: 22.33,
  1948: 24.04,
  1949: 23.81,
  1950: 24.07,
  1951: 25.96,
  1952: 26.55,
  1953: 26.77,
  1954: 26.85,
  1955: 26.77,
  1956: 27.18,
  1957: 28.09,
  1958: 28.86,
  1959: 29.15,
  1960: 29.57,
  1961: 29.89,
  1962: 30.25,
  1963: 30.62,
  1964: 31.02,
  1965: 31.51,
  1966: 32.46,
  1967: 33.36,
  1968: 34.78,
  1969: 36.68,
  1970: 38.83,
  1971: 40.49,
  1972: 41.82,
  1973: 44.4,
  1974: 49.31,
  1975: 53.82,
  1976: 56.91,
  1977: 60.61,
  1978: 65.23,
  1979: 72.57,
  1980: 82.41,
  1981: 90.93,
  1982: 96.5,
  1983: 99.6,
  1984: 103.88,
  1985: 107.57,
  1986: 109.61,
  1987: 113.62,
  1988: 118.26,
  1989: 123.97,
  1990: 130.66,
  1991: 136.19,
  1992: 140.32,
  1993: 144.46,
  1994: 148.23,
  1995: 152.38,
  1996: 156.85,
  1997: 160.52,
  1998: 163.01,
  1999: 166.58,
  2000: 172.2,
  2001: 177.07,
  2002: 179.88,
  2003: 183.96,
  2004: 188.88,
  2005: 195.29,
  2006: 201.59,
  2007: 207.34,
  2008: 215.3,
  2009: 214.54,
  2010: 218.06,
  2011: 224.94,
  2012: 229.59,
  2013: 232.96,
  2014: 236.74,
  2015: 237.02,
  2016: 240.01,
  2017: 245.12,
  2018: 251.11,
  2019: 255.66,
  2020: 258.81,
  2021: 270.97,
  2022: 292.65,
  2023: 304.7,
  2024: 313.69,
  2025: 320,
  2026: 326.02,
};

/** Last year with verified BLS data. Years beyond this are extrapolated. */
export const LAST_KNOWN_CPI_YEAR = 2026;

/** Current year (runtime). Used for "today" in comparisons and chart end. */
export function getCurrentYear(): number {
  return new Date().getFullYear();
}

/** Max year for CPI lookups and UI. Updates automatically each year. */
export const CPI_MAX_YEAR = getCurrentYear();

/** Default annual inflation rate used to extrapolate CPI for years beyond LAST_KNOWN_CPI_YEAR. */
const EXTRAPOLATION_RATE = 0.025;

export function getCPI(year: number): number | undefined {
  if (year < CPI_MIN_YEAR) return undefined;
  const stored = CPI_BY_YEAR[year];
  if (stored != null && Number.isFinite(stored)) return stored;
  // Extrapolate for years beyond our data
  if (year > LAST_KNOWN_CPI_YEAR) {
    const lastCPI = CPI_BY_YEAR[LAST_KNOWN_CPI_YEAR]!;
    return lastCPI * Math.pow(1 + EXTRAPOLATION_RATE, year - LAST_KNOWN_CPI_YEAR);
  }
  return undefined;
}

export function getCPIOrThrow(year: number): number {
  const cpi = getCPI(year);
  if (cpi == null) {
    throw new Error(`No CPI data for year ${year}`);
  }
  return cpi;
}

export const CPI_MIN_YEAR = 1913;
